<script setup>
import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCurrency } from 'dashboard/composables/useCurrency';
import KanbanCard from './KanbanCard.vue';

const props = defineProps({
  stageName: {
    type: String,
    required: true,
  },
  stageConfig: {
    type: Object,
    required: true,
  },
  conversations: {
    type: Array,
    default: () => [],
  },
  funnelId: {
    type: Number,
    required: false,
  },
  isDragOver: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'cardClick',
  'cardRemove',
  'transferToFunnel',
  'dragStart',
  'dragOver',
  'dragLeave',
  'drop',
  'toggleChecklist',
  'assignAgent',
  'winDeal',
  'loseDeal',
  'viewDetails',
  'updateValue',
  'updateAppointment',
  'sendQuickMessage',
  'addLead',
  'updateTemperature',
]);

const { t } = useI18n();
const { currency } = useCurrency();

// Renderização PROGRESSIVA: colunas com centenas de leads montavam todos os
// cards de uma vez (900+ componentes num board real) e travavam a tela por
// segundos. Monta um lote e vai montando o resto conforme a coluna rola
// (sentinela + IntersectionObserver). Totais/contadores usam a lista cheia.
const RENDER_BATCH = 25;
const renderLimit = ref(RENDER_BATCH);
const visibleConversations = computed(() =>
  props.conversations.slice(0, renderLimit.value)
);
const hiddenCount = computed(() =>
  Math.max(props.conversations.length - renderLimit.value, 0)
);

const sentinelRef = ref(null);
let sentinelObserver = null;

const renderMore = () => {
  renderLimit.value += RENDER_BATCH;
};

onMounted(() => {
  sentinelObserver = new IntersectionObserver(
    entries => {
      if (entries.some(entry => entry.isIntersecting)) renderMore();
    },
    { rootMargin: '400px' }
  );
  if (sentinelRef.value) sentinelObserver.observe(sentinelRef.value);
});

// O sentinela entra/sai do DOM conforme hiddenCount — re-observa quando surge.
watch(sentinelRef, el => {
  if (!sentinelObserver) return;
  sentinelObserver.disconnect();
  if (el) sentinelObserver.observe(el);
});

onBeforeUnmount(() => sentinelObserver?.disconnect());

// Helper method to get mapping for a conversation
const getConversationMapping = conversation => {
  return (
    conversation.funnel_mappings?.find(
      mapping => String(mapping.funnel_id) === String(props.funnelId)
    ) || {}
  );
};

// Computed properties
const columnStyle = computed(() => {
  const color = props.stageConfig.color || '#6b7280';
  // Converter hex para rgba com 10% de opacidade para cores bem suaves
  const hexToRgba = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  return {
    '--stage-color': color,
    '--header-bg-light': `color-mix(in srgb, ${color} 25%, white)`,
    '--header-bg-dark': `color-mix(in srgb, ${color} 30%, #1e293b)`,
    backgroundColor: hexToRgba(color, 0.1),
  };
});

const conversationCount = computed(() => props.conversations.length);

// Calcular o valor total - prioriza deal_value do mapping, depois Deal, depois produtos
const totalValue = computed(() => {
  let total = 0;

  props.conversations.forEach(conversation => {
    // Prioridade 1: Valor do deal_value do mapping (definido inline no Kanban)
    if (conversation.deal_value != null && conversation.deal_value !== '') {
      total += parseFloat(conversation.deal_value) || 0;
      return; // Não somar outras fontes se já tem deal_value
    }

    // Prioridade 2: Valor do Deal vinculado à conversa (se existir e for do mesmo funil)
    const deal = conversation.deal;
    if (deal && deal.amount) {
      if (
        !props.funnelId ||
        deal.funnel_id === props.funnelId ||
        String(deal.funnel_id) === String(props.funnelId)
      ) {
        total += parseFloat(deal.amount) || 0;
        return; // Não somar produtos se já tem deal
      }
    }

    // Prioridade 3: Somar produtos da conversa
    const products = conversation.conversation_products || [];

    // Filtrar produtos pelo funnel_id se disponível
    // Incluir produtos sem funnel_id (null) ou do funil atual
    const filteredProducts = props.funnelId
      ? products.filter(
          product => !product.funnel_id || product.funnel_id === props.funnelId
        )
      : products;

    // Somar apenas os produtos filtrados
    filteredProducts.forEach(product => {
      total += parseFloat(product.amount) || 0;
    });
  });

  return total;
});

// Format total value as currency
const formattedTotalValue = computed(() => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: currency.value,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(totalValue.value);
});

// Event handlers
const handleCardClick = conversationId => {
  emit('cardClick', conversationId);
};

const handleCardRemove = conversation => {
  emit('cardRemove', conversation);
};

const handleTransferToFunnel = conversation => {
  emit('transferToFunnel', conversation);
};

const handleToggleChecklist = itemId => {
  emit('toggleChecklist', itemId);
};

const handleAssignAgent = agentId => {
  emit('assignAgent', agentId);
};

const handleWinDeal = data => {
  emit('winDeal', data);
};

const handleLoseDeal = data => {
  emit('loseDeal', data);
};

const handleViewDetails = data => {
  emit('viewDetails', data);
};

const handleUpdateValue = data => {
  emit('updateValue', data);
};

const handleSendQuickMessage = data => {
  emit('sendQuickMessage', data);
};

const handleUpdateAppointment = data => {
  emit('updateAppointment', data);
};

const handleUpdateTemperature = data => {
  emit('updateTemperature', data);
};

const handleAddLead = () => {
  emit('addLead', props.stageName);
};

const handleDragStart = conversationId => {
  emit('dragStart', conversationId, props.stageName);
};

const handleDragOver = event => {
  event.preventDefault();
  emit('dragOver', event, props.stageName);
};

const handleDragLeave = event => {
  emit('dragLeave', event, props.stageName);
};

const handleDrop = event => {
  event.preventDefault();
  emit('drop', event, props.stageName);
};
</script>

<template>
  <div
    class="kanban-column flex flex-col rounded-lg min-h-full w-80 border border-slate-200/50 dark:border-slate-700/50"
    :class="[{ 'drag-over': isDragOver }]"
    :style="columnStyle"
    @dragover="handleDragOver"
    @dragleave="handleDragLeave"
    @drop="handleDrop"
  >
    <!-- Header da coluna -->
    <div
      class="column-header p-3 border-b border-slate-200 dark:border-slate-700 backdrop-blur-sm rounded-t-lg"
    >
      <!-- Primeira linha: Título, contador e valor -->
      <div class="flex items-center justify-between mb-1">
        <div class="flex items-center gap-2">
          <div
            class="w-3 h-3 rounded-full stage-indicator"
            :style="{ backgroundColor: stageConfig.color || '#6b7280' }"
          />
          <h3 class="font-semibold text-slate-800 dark:text-slate-100 text-sm">
            {{ stageConfig.label || stageName }}
          </h3>
          <span
            class="text-xs px-1.5 py-0.5 rounded-full font-medium"
            :style="{
              backgroundColor: (stageConfig.color || '#6b7280') + '30',
              color: stageConfig.color || '#6b7280',
            }"
          >
            {{ conversationCount }}
          </span>
        </div>

        <!-- Valor no canto direito -->
        <div class="flex items-center gap-2">
          <!-- SLA indicator se configurado -->
          <div
            v-if="stageConfig.sla_hours"
            class="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400"
            :title="`SLA: ${stageConfig.sla_hours}h`"
          >
            <i class="ri-time-line text-xs" />
            <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
            <span>{{ stageConfig.sla_hours }}h</span>
          </div>

          <!-- Valor simplificado -->
          <span class="text-sm font-bold text-slate-700 dark:text-slate-200">
            {{ formattedTotalValue }}
          </span>
        </div>
      </div>

      <!-- Descrição da etapa abaixo do título -->
      <p
        v-if="stageConfig.description"
        class="text-xs text-slate-500 dark:text-slate-400 pl-5"
      >
        {{ stageConfig.description }}
      </p>
    </div>

    <!-- Lista de conversas -->
    <div class="column-content flex-1 p-3 space-y-3 overflow-y-auto">
      <KanbanCard
        v-for="conversation in visibleConversations"
        :key="conversation.id"
        :conversation="conversation"
        :stage-config="stageConfig"
        :mapping="getConversationMapping(conversation)"
        :funnel-id="funnelId"
        @click="handleCardClick(conversation.id)"
        @remove-from-funnel="handleCardRemove"
        @transfer-to-funnel="handleTransferToFunnel"
        @drag-start="handleDragStart(conversation.id)"
        @toggle-checklist="handleToggleChecklist"
        @assign-agent="handleAssignAgent"
        @win-deal="handleWinDeal"
        @lose-deal="handleLoseDeal"
        @view-details="handleViewDetails"
        @update-value="handleUpdateValue"
        @update-appointment="handleUpdateAppointment"
        @send-quick-message="handleSendQuickMessage"
        @update-temperature="handleUpdateTemperature"
      />

      <!-- Sentinela: entra na viewport (400px antes) → monta o próximo lote -->
      <div
        v-if="hiddenCount > 0"
        ref="sentinelRef"
        class="flex items-center justify-center gap-2 py-3 text-xs text-slate-400"
        data-testid="kanban-column-sentinel"
      >
        <span
          class="h-4 w-4 animate-spin rounded-full border-2 border-n-alpha-2 border-t-n-brand"
        />
        {{ t('KANBAN.COLUMN.RENDERING_MORE', { count: hiddenCount }) }}
      </div>

      <!-- Empty state -->
      <div
        v-if="conversations.length === 0"
        class="text-center py-8 text-slate-400 dark:text-slate-500"
      >
        <fluent-icon icon="chat" size="24" class="mb-2" />
        <p class="text-sm">{{ t('KANBAN.NO_CONVERSATIONS') }}</p>
      </div>

      <!-- Botão Adicionar Lead -->
      <button
        class="w-full mt-2 py-2 px-3 rounded-lg border-2 border-dashed border-slate-300 dark:border-slate-600 text-slate-500 dark:text-slate-400 hover:border-slate-400 dark:hover:border-slate-500 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-all flex items-center justify-center gap-2"
        @click="handleAddLead"
      >
        <i class="ri-add-line text-lg" />
        <span class="text-sm font-medium">
          {{ t('KANBAN.COLUMN.NEW_LEAD') }}
        </span>
      </button>
    </div>
  </div>
</template>

<style scoped>
.kanban-column {
  min-width: 320px;
  max-width: 320px;
  transition: all 0.2s ease;
}

.kanban-column.drag-over {
  opacity: 0.9;
  transform: scale(1.01);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.column-header {
  background-color: var(--header-bg-light);
}

:root.dark .column-header,
.dark .column-header {
  background-color: var(--header-bg-dark);
}

.column-content {
  max-height: calc(100vh - 200px);
  min-height: 400px;
}

.column-content::-webkit-scrollbar {
  width: 4px;
}

.column-content::-webkit-scrollbar-track {
  background: transparent;
}

.column-content::-webkit-scrollbar-thumb {
  background: rgba(148, 163, 184, 0.4);
  border-radius: 2px;
}

.column-content::-webkit-scrollbar-thumb:hover {
  background: rgba(148, 163, 184, 0.6);
}

.stage-indicator {
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.9);
}

:root.dark .stage-indicator,
.dark .stage-indicator {
  box-shadow: 0 0 0 2px rgba(30, 41, 59, 0.9);
}
</style>
